import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from 'vue';
import TimeAgo from '@/components/TimeAgo.vue';
import InfiniteScroll from '@/components/list/InfiniteScroll.vue';
import ProviderTimeline from '@/components/timeline/ProviderTimeline.vue';
import TimelineAdUnit from '@/components/timeline/TimelineAdUnit.vue';
import TimelineHeader from '@/components/timeline/TimelineHeader.vue';
import { BucketData, FormattedTimelineData } from '@/interfaces/new-title-graphql';
import { TitleEventPayloadGraphql } from '@/helpers/title-helper';
import { NewDateRangeBucket, NewPageType, NewTitlesEdge } from '@/@types/graphql-types';
import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { useI18n } from '@/helpers/composables/useI18n';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/pinia/useUserStore';

export default /*#__PURE__*/_defineComponent({
  __name: 'TitleTimeline',
  props: {
    formattedTimelineData: null,
    newPageType: null,
    isLoading: { type: Boolean },
    isFirstPage: { type: Boolean },
    isAtEndOfList: { type: Boolean }
  },
  emits: ["onScrollToTop", "onPosterClick", "mounted", "passUpTitles", "load-next"],
  setup(__props: any, { expose, emit }: { emit: ({
	(e: 'onScrollToTop'): void;
	(e: 'onPosterClick', payload: TitleEventPayloadGraphql): void;
	(e: 'mounted'): void;
	(e: 'passUpTitles', titles: NewTitlesEdge[]): void;
	(e: 'load-next', payload: { event: any }): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	formattedTimelineData: FormattedTimelineData[];
	newPageType: NewPageType;
	isLoading?: boolean;
	isFirstPage?: boolean;
	isAtEndOfList?: boolean;
};

const TimelineEmptyState = () =>
	import(/* webpackChunkName: "timeline-empty-state" */ '@/components/timeline/TimelineEmptyState.vue');





const { isPremium } = storeToRefs(useUserStore());
const { t } = useI18n();

const allTitles = ref<NewTitlesEdge[]>([]);
const allNewTitles = computed(() => allTitles.value.filter(Boolean).flat());
const isFetchedListEmpty = computed(() => props.formattedTimelineData.length === 0);
const showEmptyStateContent = computed(() => !props.isLoading && isFetchedListEmpty.value);
const isLeavingSoonOrComingSoon = computed(() =>
	[NewPageType.LeavingSoon, NewPageType.Upcoming].includes(props.newPageType)
);
const infiniteScrollDisabled = computed(() => isFetchedListEmpty.value || props.isAtEndOfList);
const infiniteScroll = ref<InstanceType<typeof InfiniteScroll> | null>(null);

const showEmptyTimeline = computed(() => {
	if (props.isLoading) return false;

	const showEmptyTimeline = showEmptyStateContent.value || props.isAtEndOfList;

	if (isLeavingSoonOrComingSoon.value) {
		return showEmptyTimeline && isPremium.value;
	}
	return showEmptyTimeline;
});

function getIndexByProvider(dayIndex: number, providerIndex: number) {
	if (dayIndex > 0) {
		[...Array(dayIndex).keys()].forEach(i => {
			const item = props.formattedTimelineData[i + 1];
			if (item) {
				providerIndex += item.providers.length;
			}
		});
	}
	return providerIndex;
}

function getBucketDate(bucket: BucketData) {
	return bucket.key?.date ?? '';
}

function getBucketType(bucket: FormattedTimelineData) {
	return !bucket.isGroupedByDate ? (bucket.key as NewDateRangeBucket) : null;
}

function getBucketTranslatable(bucketKey: string) {
	const translationMap: Record<NewDateRangeBucket, string> = {
		[NewDateRangeBucket.Today]: 'WEBAPP_TODAY',
		[NewDateRangeBucket.Tomorrow]: 'WEBAPP_DATE_TOMORROW',
		[NewDateRangeBucket.Next_7Days]: 'WEBAPP_BADGE_IN_DAYS',
		[NewDateRangeBucket.Next_30Days]: 'WEBAPP_BADGE_IN_DAYS',
	};

	const days = bucketKey === NewDateRangeBucket.Next_7Days ? 7 : 30;

	return t(translationMap[bucketKey as NewDateRangeBucket], { days });
}
// Resets the internal state of the infinite scroll. Needs to be called at each filter change.
function resetInfiniteScroll() {
	infiniteScroll.value?.reset();
}

watch(allNewTitles, () => {
	emit('passUpTitles', allNewTitles.value);
});

useProvideClickoutPlacement('title_timeline');

onMounted(() => {
	emit('mounted');
});

expose({
	resetInfiniteScroll,
});

return { __sfc: true,TimelineEmptyState, props, emit, isPremium, t, allTitles, allNewTitles, isFetchedListEmpty, showEmptyStateContent, isLeavingSoonOrComingSoon, infiniteScrollDisabled, infiniteScroll, showEmptyTimeline, getIndexByProvider, getBucketDate, getBucketType, getBucketTranslatable, resetInfiniteScroll, TimeAgo, InfiniteScroll, ProviderTimeline, TimelineAdUnit, TimelineHeader }
}

})